import React, { useEffect, useState } from "react";
import useInventoryApi from "./useInventoryApi";
import "./Inventory.css";

// Real compliance surface: the FDACS pesticide application log
// (GET /inventory/reports/fdacs-application-log/). Rows appear as field techs
// log chemical applications. SDS / lot-expiry tracking has no backend yet, so
// it is honestly absent rather than mocked.
export default function CompliancePage() {
  const { fdacsLog } = useInventoryApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fdacsLog()
      .then((r) => { if (live) setRows(r); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [fdacsLog]);

  const downloadCsv = () => {
    // Build the CSV client-side from the already-fetched (auth-scoped) rows,
    // so no unauthenticated window.open request is made.
    const cols = [
      "application_date", "product_name", "epa_reg_number", "active_ingredient",
      "quantity", "unit", "dilution_rate", "application_method",
      "target_pest", "site_area", "applicator",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(",")]
      .concat(rows.map((r) => cols.map((c) => esc(r[c])).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fdacs_application_log.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="inventory-page">
      <header className="inventory-head">
        <div>
          <h1>Chemical Compliance</h1>
          <p className="muted">
            FDACS pesticide application log (FL Ch. 487) — one row per chemical
            applied, from field capture.
          </p>
        </div>
        <div className="filters">
          <button className="btn mini" onClick={downloadCsv} disabled={!rows.length}>
            Download CSV
          </button>
        </div>
      </header>

      <section className="inventory-card">
        <div
          className="inventory-table"
          style={{ "--inventory-columns": "0.9fr 1.3fr 1fr 0.9fr 0.8fr 1.1fr" }}
        >
          <div className="inventory-thead">
            <div>Date</div>
            <div>Product</div>
            <div>EPA Reg #</div>
            <div>Rate</div>
            <div>Site</div>
            <div>Applicator</div>
          </div>
          {loading ? (
            <div className="inventory-row muted">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="inventory-empty">
              <div className="value" style={{ fontSize: 15 }}>No applications logged yet</div>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                Chemical applications logged by techs (product, EPA #, rate,
                site, applicator) appear here and export to the FDACS format.
              </p>
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.treatment_id} className="inventory-row">
                <div>{r.application_date}</div>
                <div>{r.product_name}</div>
                <div className="mono">{r.epa_reg_number}</div>
                <div>
                  {r.quantity != null ? `${r.quantity} ${r.unit || ""}`.trim() : "—"}
                  {r.dilution_rate ? ` · ${r.dilution_rate}` : ""}
                </div>
                <div>{r.site_area || "—"}</div>
                <div>{r.applicator || "—"}</div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
